// @ts-nocheck
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'lodash';
import { DataTable } from '@/components/organism/Table';
import FullPageCard from '@/components/organism/Cards/FullPageCard';
import PageToolbar from '@/components/organism/Cards/PageToolbar';
import SegmentedControl from '@/components/elements/SegmentedControl';
import Autocomplete from '@/components/elements/AutoComplete';
import CommonDropdownSelector from '@/components/organism/GroupDropdown';
import DownloadReport from '@/components/organism/ExportDataModal/DownloadReport';
import { getSelectedTimeZone } from '@/utils/dateTime';
import {
  ROW_PER_PAGE_OPTION,
  pagination as PAGINATION_INITIAL_STATE,
  EActiveStatus,
  STRINGS,
  ALL_PROVIDER,
  DROPDOWN_OPTIONS,
  REPORT_FILE_NAMES,
  MODAL_TITLES,
  SortEnum,
} from '@/constant/global/global.constant';
import { clearMarketError, getMarkets } from '@/store/slices/market.slice';
import { getMarketDetail } from '@/store/selectors/market.selector';
import {
  EReportFormat,
  EReportLabel,
} from '@/constant/downloadReport/downloadReport.const';
import { API_URL_CONFIG } from '@/config/urlConfig/apiUrlConfig';
import { DEVICE_PROPS } from '@/constant/device/device.constant';
import {
  EExportPage,
  EAllowedFieldsForExport,
} from '@/components/organism/ExportDataModal/export.types';
import { useExportFormats } from '@/hooks/useExportFormats';
import { useDateFilter } from '@/context/dateFilter.context';
import useDeepEffect from '@/hooks/useDeepEffect';
import { MARKET_PROPS } from '@/constant/market/market.constant';
import { NAV_ITEMS, getNavItemIcon } from '@/constant/layout/layout.constant';
import { ERequestMethods } from '@/constant/downloadReport/downloadReport';
import { tid } from '@/utils/testId';
import { MARKET_TEST_IDS as TID } from '@/constant/testIds/market.testIds';
import { buildColumns } from './market.columns';

/* ── Page ─────────────────────────────────────────────────────────────────── */
const Markets = () => {
  const dispatch = useDispatch();
  const selectedTimezone = getSelectedTimeZone();
  const exportFormats = useExportFormats(EExportPage.MARKETS);
  const { filterValues } = useDateFilter();
  const hasLoaded = useRef(false);

  const [isCopy, setIsCopy] = useState('');
  const [activeStatus, setActiveStatus] = useState<string>('1');
  const [sorting, setSorting] = useState({
    orderBy: 'updatedOn',
    order: SortEnum.desc,
  });
  const [pagination, setPagination] = useState(PAGINATION_INITIAL_STATE);
  const [locationDetail, setLocationDetails] = useState({
    provider: ALL_PROVIDER,
    location: DROPDOWN_OPTIONS.ALL_LOCATION_DROPDOWN_OPTION,
    operator: DROPDOWN_OPTIONS.ALL_OPERATOR_DROPDOWN_OPTION,
  });

  const {
    loading,
    markets: { data, pagination: paginationResponse },
  } = useSelector(getMarketDetail);

  // Track first successful data load
  useEffect(() => {
    if (!hasLoaded.current && Array.isArray(data) && data.length > 0) {
      hasLoaded.current = true;
    }
  }, [data]);

  const totalPages = Math.ceil(
    (paginationResponse?.totalRecords || 0) / pagination.limit,
  );

  const onCopyClick = useCallback((key: string) => {
    setIsCopy(key);
    setTimeout(() => setIsCopy(''), 500);
  }, []);

  const handleLocationDetails = useCallback((val: any) => {
    setLocationDetails({
      provider: val?.provider,
      operator: val?.operator,
      location: val?.location,
    });
    setPagination((p) => ({ ...p, page: 1 }));
  }, []);

  const handlePageSizeChange = useCallback(
    (value: number) => {
      setPagination((p) => ({
        ...p,
        limit: value,
        page: totalPages < p.page ? totalPages : p.page,
      }));
    },
    [totalPages],
  );

  const handlePreviousClick = useCallback((page: number) => {
    if (page > 1) setPagination((p) => ({ ...p, page: page - 1 }));
  }, []);

  const handleNextClick = useCallback(
    (page: number) => {
      if (page < totalPages) setPagination((p) => ({ ...p, page: page + 1 }));
    },
    [totalPages],
  );

  const fetchMarkets = useCallback(() => {
    dispatch(
      getMarkets({
        ...pagination,
        startDate: filterValues?.startDate,
        endDate: filterValues?.endDate,
        ...sorting,
        operatorId: locationDetail?.operator?.id,
        locationId: locationDetail?.location?.id,
        includeInactive: activeStatus === 'all' ? true : undefined,
        inactive: activeStatus === '0' ? true : undefined,
      }),
    );
  }, [pagination, filterValues, sorting, locationDetail, activeStatus]);

  useEffect(() => {
    if (pagination?.page > totalPages && totalPages !== 0) {
      setPagination((p) => ({ ...p, page: 1 }));
    }
  }, [totalPages, pagination?.page]);

  useDeepEffect(
    () => {
      fetchMarkets();
    },
    [
      pagination?.limit,
      pagination?.page,
      locationDetail,
      sorting,
      filterValues?.startDate,
      filterValues?.endDate,
      activeStatus,
    ],
    true,
  );

  useEffect(() => {
    return () => {
      dispatch(clearMarketError());
    };
  }, [dispatch]);

  const columns = useMemo(
    () => buildColumns(isCopy, onCopyClick),
    [isCopy, onCopyClick],
  );
  const allowedFields = EAllowedFieldsForExport[EExportPage.MARKETS] || [];

  return (
    <FullPageCard
      testId={tid(TID.PAGE)}
      className="rounded-b-none"
      header={
        <PageToolbar
          title={STRINGS.MARKET}
          subtitle={STRINGS.MARKET_SUB}
          icon={getNavItemIcon(NAV_ITEMS.MARKETS)}
          actionsSlot={
            <DownloadReport
              testId={tid(TID.EXPORT_BTN)}
              label={EReportLabel.EXPORT_DATA}
              type={EReportFormat.CSV}
              url={API_URL_CONFIG?.MARKET?.GET_DOWNLOAD_PDF_CSV}
              orderBy={sorting?.orderBy}
              order={sorting?.order}
              locationId={locationDetail?.location?.id}
              operatorId={locationDetail?.operator?.id}
              startDate={filterValues?.startDate}
              endDate={filterValues?.endDate}
              includeInactive={activeStatus === 'all' ? true : undefined}
              inactive={activeStatus === '0' ? true : undefined}
              limit={paginationResponse?.totalRecords}
              timeZone={selectedTimezone}
              fileName={REPORT_FILE_NAMES.MARKETS_REPORT}
              modalTitle={MODAL_TITLES.MARKETS_PAGE}
              supportingReportFormats={exportFormats}
              requestMethod={ERequestMethods.POST}
              selectiveFieldExport
              allowedFieldsForExport={allowedFields}
            />
          }
          filtersLeft={
            <CommonDropdownSelector
              onSelectionChange={handleLocationDetails}
              showProvider={false}
              showOperator
              showLocation
              dataTestId={tid(TID.FILTER_DROPDOWN)}
            />
          }
          filtersRight={
            <SegmentedControl
              testId={tid(TID.STATUS_FILTER)}
              size="lg"
              shape="square"
              variant="gradient"
              options={[
                { value: 'all', label: MARKET_PROPS.FILTERS.STATUS_ALL },
                { value: '1', label: MARKET_PROPS.FILTERS.STATUS_ACTIVE },
                { value: '0', label: MARKET_PROPS.FILTERS.STATUS_INACTIVE },
              ]}
              value={activeStatus}
              onChange={(v: string) => {
                setActiveStatus(v);
                setPagination((p) => ({ ...p, page: 1 }));
              }}
            />
          }
        />
      }
    >
      <DataTable
        data-testid={tid(TID.TABLE)}
        data={data ?? []}
        columns={columns}
        loading={loading}
        skeletonRows={15}
        onSort={(orderBy, order) =>
          setSorting({ orderBy, order: order as SortEnum })
        }
        sortState={sorting}
        rowKey={(item: any) => item?.marketId ?? ''}
        rowTestId={(item: any, idx: number) =>
          TID.TABLE_ROW(`${idx}`)
        }
        pagination={
          !isEmpty(data)
            ? {
                state: pagination,
                totalItem: paginationResponse?.totalRecords,
                rowsPerPageOptions: ROW_PER_PAGE_OPTION,
                onRowsPerPageChange: handlePageSizeChange,
                onNextClick: handleNextClick,
                onPreviousClick: handlePreviousClick,
              }
            : null
        }
      />
    </FullPageCard>
  );
};

export default Markets;